import { InputHTMLAttributes, forwardRef, type ReactNode } from "react"
import { cn } from "@/lib/utils"
import { Input } from "@/components/ui/input"
import { Icon } from "@/components/ui/icon"

interface FormInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  label: string
  error?: string
  icon?: string
  requiredLabel?: boolean
  inputPrefix?: ReactNode
  helperText?: ReactNode
  /** "default" = h-14, "sm" = compacto (filtros / blocos inline) */
  size?: "default" | "sm"
  containerClassName?: string
}

export const FormInput = forwardRef<HTMLInputElement, FormInputProps>(
  (
    {
      className,
      containerClassName,
      label,
      error,
      icon,
      requiredLabel,
      inputPrefix,
      helperText,
      value,
      onChange,
      type,
      size = "default",
      ...props
    },
    ref,
  ) => {
    return (
      <label className={cn("flex flex-col w-full gap-2", containerClassName)}>
        {(label || requiredLabel) && (
          <span className="text-sm font-bold text-foreground ml-1">
            {label} {requiredLabel && <span className="text-destructive">*</span>}
          </span>
        )}
        <div className="relative group">
          {inputPrefix && (
            <span
              className={cn(
                "absolute top-1/2 -translate-y-1/2 text-foreground font-bold z-10 pointer-events-none",
                size === "sm" ? "left-4 sm:left-3.5" : "left-5",
              )}
            >
              {inputPrefix}
            </span>
          )}
          <Input
            ref={ref}
            size={size}
            className={cn(
              icon ? "pr-12" : undefined,
              inputPrefix && (size === "sm" ? "pl-11 sm:pl-10" : "pl-12"),
              className
            )}
            type={type}
            value={value !== undefined && value !== null ? String(value) : ""}
            onChange={onChange}
            {...props}
          />
          {icon && (
            <Icon name={icon} />
          )}
        </div>
        {error && (
          <p className="text-destructive text-sm font-medium ml-1">{error}</p>
        )}
        {helperText && (
          <p className="text-xs text-muted-foreground ml-1">{helperText}</p>
        )}
      </label>
    )
  }
)
FormInput.displayName = "FormInput"
